import React, { Fragment, useEffect, useRef, useState } from "react";
import { getLots, getLotSelectionData } from "../../api/lot.api";
import { bulkAssignAddOnLots } from "../../api/addon.api";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
  Button,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { toast } from "react-toastify";

const BulkAssign = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [fetchingLots, setFetchingLots] = useState(false);
  const [selectionData, setSelectionData] = useState({
    locations: [],
  });

  const [filters, setFilters] = useState({
    keyword: "",
    location: "all",
    type: "all",
  });

  const [selectedLots, setSelectedLots] = useState([]);

  const hasFilter = true;

  const loadSelectionData = async () => {
    try {
      const data = await getLotSelectionData();

      setSelectionData({
        locations: data.locations || [],
      });
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    loadSelectionData();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyChange = (lotId, checked) => {
    setSelectedLots((prev) =>
      prev.map((lot) =>
        lot.id === lotId
          ? {
              ...lot,
              apply: checked,
            }
          : lot
      )
    );
  };

  const handleLoadLots = async () => {
    if (!hasFilter) {
      return toast.error("Please select at least one filter");
    }

    try {
      setFetchingLots(true);

      const res = await getLots({
        page: 1,
        perPage: 1000,
        sortField: "location",
        sortOrder: "ASC",
        filters: {
          ...(filters.keyword?.trim()
            ? { keyword: filters.keyword.trim() }
            : {}),
          ...(filters.location !== "all"
            ? { location: filters.location }
            : {}),
          ...(filters.type !== "all" ? { type: filters.type } : {}),
        },
      });

      setSelectedLots((prev) => {
        const existingIds = new Set(prev.map((lot) => lot.id));

        const newLots = (res?.data || [])
          .filter((lot) => !existingIds.has(lot.id))
          .map((lot) => {
            const existingAddOn = lot.lotAddOns?.find(
              (item) => Number(item.addOnId) === Number(id)
            );

            return {
              ...lot,
              apply: Boolean(existingAddOn),
              wasApplied: Boolean(existingAddOn),
              lotAddOnId: existingAddOn?.id || null,
              minQty:
                existingAddOn?.minQty !== undefined &&
                existingAddOn?.minQty !== null
                  ? existingAddOn.minQty
                  : "",
              maxQty:
                existingAddOn?.maxQty !== undefined &&
                existingAddOn?.maxQty !== null
                  ? existingAddOn.maxQty
                  : "",
              isEventPass: Boolean(existingAddOn?.isEventPass),
            };
          });

        return [...prev, ...newLots];
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFetchingLots(false);
    }
  };

  const handleLotQtyChange = (lotId, field, value) => {
    setSelectedLots((prev) =>
      prev.map((lot) =>
        lot.id === lotId
          ? {
              ...lot,
              [field]: value,
            }
          : lot
      )
    );
  };

  const removeLot = (lotId) => {
    setSelectedLots((prev) => prev.filter((lot) => lot.id !== lotId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (selectedLots.length === 0) {
        return toast.error("Please select at least one lot");
      }

      const payload = {
        addOnId: Number(id),

        assignLots: selectedLots
          .filter((lot) => lot.apply)
          .map((lot) => ({
            lotId: lot.id,
            minQty: lot.minQty !== "" ? Number(lot.minQty) : 0,
            maxQty: lot.maxQty !== "" ? Number(lot.maxQty) : null,
            isEventPass: !!lot.isEventPass,
          })),

        unassignLots: selectedLots
          .filter((lot) => lot.wasApplied && !lot.apply)
          .map((lot) => ({
            lotId: lot.id,
            lotAddOnId: lot.lotAddOnId,
          })),
      };

      setLoading(true);

      await bulkAssignAddOnLots(id, payload);

      toast.success("Bulk assign completed successfully");
      navigate(`/inventory/view/${id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("inventory"))}
        title={titleCase(t("bulk assign"))}
        mainTitle={titleCase(t("bulk assign"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Form innerRef={formRef} onSubmit={handleSubmit}>
              <Card className="mb-4">
                <CardHeader>
                  <H5>{titleCase(t("search lots"))}</H5>
                </CardHeader>

                <CardBody>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("lot name"))}</Label>
                        <Input
                          type="text"
                          value={filters.keyword}
                          onChange={(e) =>
                            handleFilterChange("keyword", e.target.value)
                          }
                          placeholder={titleCase(t("search lot name"))}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("location"))}</Label>

                        <Input
                          type="select"
                          value={filters.location}
                          onChange={(e) =>
                            handleFilterChange("location", e.target.value)
                          }
                        >
                          <option value="all">{titleCase(t("all"))}</option>

                          {selectionData.locations.map((location) => (
                            <option key={location} value={location}>
                              {location}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="3">
                      <FormGroup>
                        <Label>{titleCase(t("type"))}</Label>
                        <Input
                          type="select"
                          value={filters.type}
                          onChange={(e) =>
                            handleFilterChange("type", e.target.value)
                          }
                        >
                          <option value="all">{titleCase(t("all"))}</option>
                          <option value="permanent">
                            {titleCase(t("permanent"))}
                          </option>
                          <option value="temporary">
                            {titleCase(t("temporary"))}
                          </option>
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md="1" className="d-flex align-items-center">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleLoadLots}
                        disabled={fetchingLots}
                      >
                        {fetchingLots
                          ? `${titleCase(t("loading"))}...`
                          : titleCase(t("load"))}
                      </button>
                    </Col>
                  </Row>

                  {fetchingLots && (
                    <p className="text-muted mb-0">
                      {titleCase(t("loading"))}...
                    </p>
                  )}
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <H5>{titleCase(t("assign inventory"))}</H5>
                </CardHeader>

                <CardBody>
                  {selectedLots.length > 0 ? (
                    <div className="table-responsive">
                      <Table bordered>
                        <thead>
                          <tr>
                            <th>{titleCase(t("lot name"))}</th>
                            <th>{titleCase(t("location"))}</th>
                            <th>{titleCase(t("type"))}</th>
                            <th>{titleCase(t("capacity"))}</th>
                            <th>{titleCase(t("minimum quantity"))}</th>
                            <th>{titleCase(t("maximum quantity"))}</th>
                            <th>{titleCase(t("event pass"))}</th>
                            <th>{titleCase(t("apply"))}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {selectedLots.map((lot) => (
                            <tr key={lot.id}>
                              <td>{lot.name}</td>
                              <td>{lot.location || "-"}</td>
                              <td>
                                {titleCase(t(lot.type || "permanent"))}
                              </td>
                              <td>{lot.capacity ?? "-"}</td>
                              <td>
                                <Input
                                  type="number"
                                  min="0"
                                  value={lot.minQty}
                                  disabled={!lot.apply}
                                  onChange={(e) =>
                                    handleLotQtyChange(
                                      lot.id,
                                      "minQty",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>

                              <td>
                                <Input
                                  type="number"
                                  min="0"
                                  value={lot.maxQty}
                                  disabled={!lot.apply}
                                  onChange={(e) =>
                                    handleLotQtyChange(
                                      lot.id,
                                      "maxQty",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>

                              <td className="text-center">
                                <FormGroup className="custom-radio-ml mb-0">
                                  <div className="checkbox checkbox-primary">
                                    <Input
                                      id={`event-pass-lot-${lot.id}`}
                                      type="checkbox"
                                      checked={!!lot.isEventPass}
                                      disabled={!lot.apply}
                                      onChange={(e) =>
                                        handleLotQtyChange(
                                          lot.id,
                                          "isEventPass",
                                          e.target.checked
                                        )
                                      }
                                    />

                                    <Label
                                      for={`event-pass-lot-${lot.id}`}
                                      className="mb-0"
                                    />
                                  </div>
                                </FormGroup>
                              </td>

                              <td className="text-center">
                                <FormGroup className="custom-radio-ml mb-0">
                                  <div className="checkbox checkbox-primary">
                                    <Input
                                      id={`apply-lot-${lot.id}`}
                                      type="checkbox"
                                      checked={lot.apply}
                                      onChange={(e) =>
                                        handleApplyChange(lot.id, e.target.checked)
                                      }
                                    />

                                    <Label for={`apply-lot-${lot.id}`} className="mb-0">
                                    </Label>
                                  </div>
                                </FormGroup>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">
                      {titleCase(t("no matching lots found"))}
                    </p>
                  )}
                </CardBody>
              </Card>
            </Form>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default BulkAssign;